import { GameState, getCurrentPlayer } from './GameState';
import { GameAction } from './GameActions';
import { Card, isWild, isJoker, getCardPoints, sortHand } from '../utils/CardUtils';
import { validateMeld, canAddToMeld, isBurraco, MeldType } from './Meld';

export function getAIAction(state: GameState): GameAction {
  const player = getCurrentPlayer(state);

  if (state.turnPhase === 'draw') {
    return decideDrawAction(state);
  }

  // Try to meld
  if (state.turnPhase === 'meld' || state.hasDrawnThisTurn) {
    const meldAction = decideMeldAction(state);
    if (meldAction) return meldAction;
  }

  // Discard
  return decideDiscardAction(state);
}

function decideDrawAction(state: GameState): GameAction {
  // Evaluate top of discard pile
  if (state.discardPile.length > 0) {
    const topDiscard = state.discardPile[state.discardPile.length - 1];
    const player = getCurrentPlayer(state);

    // Check if the top card helps complete a meld or add to existing
    const handWithDiscard = [...player.hand, topDiscard];

    // Check if it enables a new meld
    const potentialMelds = findAllMelds(handWithDiscard);
    const currentMelds = findAllMelds(player.hand);

    if (potentialMelds.length > currentMelds.length) {
      // Taking discard enables a new meld
      // But only take entire pile if it's small or the benefit is high
      if (state.discardPile.length <= 5 || potentialMelds.length > currentMelds.length + 1) {
        return { type: 'DRAW_DISCARD' };
      }
    }

    // Check if it can be added to existing melds
    for (const meld of player.melds) {
      if (canAddToMeld(meld, topDiscard)) {
        if (state.discardPile.length <= 3) {
          return { type: 'DRAW_DISCARD' };
        }
      }
    }
  }

  return { type: 'DRAW_DECK' };
}

function decideMeldAction(state: GameState): GameAction | null {
  const player = getCurrentPlayer(state);

  // Try adding cards to existing melds first (always good)
  for (const meld of player.melds) {
    for (const card of player.hand) {
      if (canAddToMeld(meld, card)) {
        // Prefer adding to melds that are close to burraco
        if (meld.cards.length >= 5 || player.hand.length > 5) {
          return { type: 'ADD_TO_MELD', cardId: card.id, meldId: meld.id };
        }
      }
    }
  }

  // Find best meld to play
  const melds = findAllMelds(player.hand);

  if (melds.length > 0) {
    // In early game (many cards), be more conservative
    // In late game (few cards), meld everything
    const shouldMeld = player.hand.length <= 6 || melds.some(m => m.length >= 5);

    if (shouldMeld) {
      // Pick the best meld (longest first, then runs over sets)
      melds.sort((a, b) => b.length - a.length);
      const bestMeld = melds[0];
      return { type: 'PLAY_MELD', cardIds: bestMeld.map(c => c.id) };
    }
  }

  return null;
}

function decideDiscardAction(state: GameState): GameAction {
  const player = getCurrentPlayer(state);

  // Score each card: higher score = more desirable to discard
  const scored = player.hand.map(card => ({
    card,
    score: evaluateDiscardScore(card, player.hand, state),
  }));

  // Sort by score descending (highest = most want to discard)
  scored.sort((a, b) => b.score - a.score);

  // Don't discard wilds if possible
  for (const { card } of scored) {
    if (!isWild(card)) {
      return { type: 'DISCARD', cardId: card.id };
    }
  }

  // Must discard something even if all wilds
  return { type: 'DISCARD', cardId: scored[0].card.id };
}

function evaluateDiscardScore(card: Card, hand: Card[], state: GameState): number {
  let score = 0;

  // Wilds are very valuable — never discard
  if (isWild(card)) return -1000;

  // Isolated cards (not part of any potential meld) are good to discard
  const otherCards = hand.filter(c => c.id !== card.id);
  const meldsWithout = findAllMelds(otherCards);
  const meldsWith = findAllMelds(hand);

  if (meldsWith.length > meldsWithout.length) {
    // This card is part of a potential meld — don't discard
    score -= 50;
  }

  // Check if card is part of a partial run (2 consecutive same-suit cards)
  const samesuit = otherCards.filter(c => c.suit === card.suit && !isJoker(c));
  const hasNeighbor = samesuit.some(c => Math.abs(c.rank - card.rank) === 1);
  if (hasNeighbor) score -= 20;

  // Check if card is part of a partial set (2 same-rank cards)
  const sameRank = otherCards.filter(c => c.rank === card.rank && !isJoker(c));
  if (sameRank.length >= 1) score -= 15;

  // Higher point cards are worse to keep in hand (penalty at round end)
  score += getCardPoints(card) * 0.5;

  // Prefer discarding cards the opponent might not need
  // Simple heuristic: cards that have already been discarded are less useful
  const discardedSameRank = state.discardPile.filter(c => c.rank === card.rank).length;
  score += discardedSameRank * 5;

  return score;
}

function findAllMelds(cards: Card[]): Card[][] {
  const melds: Card[][] = [];

  // Find sets (same rank)
  const byRank = new Map<number, Card[]>();
  for (const card of cards) {
    if (isJoker(card)) continue;
    if (!byRank.has(card.rank)) byRank.set(card.rank, []);
    byRank.get(card.rank)!.push(card);
  }

  for (const [, rankCards] of byRank) {
    if (rankCards.length >= 3) {
      melds.push(rankCards.slice(0, Math.min(rankCards.length, 4)));
    }
  }

  // Find runs (same suit, consecutive)
  const bySuit = new Map<string, Card[]>();
  for (const card of cards) {
    if (isJoker(card)) continue;
    if (!bySuit.has(card.suit)) bySuit.set(card.suit, []);
    bySuit.get(card.suit)!.push(card);
  }

  for (const [, suitCards] of bySuit) {
    const sorted = [...suitCards].sort((a, b) => a.rank - b.rank);
    let run: Card[] = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].rank === sorted[i - 1].rank + 1) {
        run.push(sorted[i]);
      } else {
        if (run.length >= 3) melds.push([...run]);
        run = [sorted[i]];
      }
    }
    if (run.length >= 3) melds.push([...run]);
  }

  return melds;
}
